export type ZoomPreset = "day" | "week" | "month";
export type TickMode = ZoomPreset;

/** Preset px/day values at 100%. */
export const ZOOM_PRESET_PX: Record<ZoomPreset, number> = {
  day: 34,
  week: 11,
  month: 3.6,
};

/** Default preset (week) = baseline for migration when nothing is stored. */
export const ZOOM_BASE_PX = ZOOM_PRESET_PX.week;

/** Gentle continuous zoom step (± ~8% per click / wheel notch). */
export const ZOOM_STEP = 1.08;

export const ZOOM_PERCENT_OPTIONS = [50, 75, 90, 100, 125, 150, 200] as const;

export const PX_PER_DAY_MIN = 0.8;
export const PX_PER_DAY_MAX = 120;

export function isZoomPreset(v: unknown): v is ZoomPreset {
  return v === "day" || v === "week" || v === "month";
}

export function clampPxPerDay(px: number): number {
  if (!Number.isFinite(px)) return ZOOM_BASE_PX;
  return Math.min(PX_PER_DAY_MAX, Math.max(PX_PER_DAY_MIN, px));
}

export function clampZoomPercent(pct: number): number {
  if (!Number.isFinite(pct)) return 100;
  return Math.min(400, Math.max(25, Math.round(pct)));
}

/** Percent relative to the active day/week/month preset (100% = preset px). */
export function zoomPercent(pxPerDay: number, preset: ZoomPreset): number {
  const base = ZOOM_PRESET_PX[preset];
  return clampZoomPercent((clampPxPerDay(pxPerDay) / base) * 100);
}

export function pxPerDayFromPercent(preset: ZoomPreset, percent: number): number {
  return clampPxPerDay(ZOOM_PRESET_PX[preset] * (clampZoomPercent(percent) / 100));
}

export function nudgePxPerDay(pxPerDay: number, direction: 1 | -1): number {
  const next = direction > 0 ? pxPerDay * ZOOM_STEP : pxPerDay / ZOOM_STEP;
  return clampPxPerDay(next);
}

/** Wheel: smaller steps; deltaY magnitude adds at most a few notches. */
export function wheelZoomFactor(deltaY: number): number {
  const notches = Math.min(2.5, Math.max(0.35, Math.abs(deltaY) / 120));
  const step = Math.pow(ZOOM_STEP, notches);
  return deltaY < 0 ? step : 1 / step;
}

export function resolveZoomPreset(ui: { zoomPreset?: unknown; zoom?: unknown; pxPerDay?: unknown } | null | undefined): ZoomPreset {
  if (ui && isZoomPreset(ui.zoomPreset)) return ui.zoomPreset;
  if (ui && isZoomPreset(ui.zoom)) return ui.zoom;
  if (ui && typeof ui.pxPerDay === "number") {
    const px = clampPxPerDay(ui.pxPerDay);
    let best: ZoomPreset = "week";
    let bestRel = Infinity;
    for (const z of ["day", "week", "month"] as ZoomPreset[]) {
      const rel = Math.abs(px - ZOOM_PRESET_PX[z]) / ZOOM_PRESET_PX[z];
      if (rel < bestRel) {
        bestRel = rel;
        best = z;
      }
    }
    return best;
  }
  return "week";
}

export function resolvePxPerDay(ui: { pxPerDay?: unknown; zoom?: unknown; zoomPreset?: unknown } | null | undefined): number {
  if (ui && typeof ui.pxPerDay === "number") return clampPxPerDay(ui.pxPerDay);
  const preset = resolveZoomPreset(ui);
  return ZOOM_PRESET_PX[preset];
}
